import {
  showSuccess,
  showConfirm,
  showCustom,
} from "@/assets/js/utils/alertService";
import api from "@/assets/js/utils/api";
import { DateHelper } from "@/assets/js/utils/time";

export default {
  name: "PermissionManagement",

  data() {
    return {
      searchQuery: "",
      activeFilter: "all",
      showAddModal: false,
      showPermissionModal: false,
      editingRole: null,
      selectedRole: null,
      formData: {
        RoleName: "",
        IsActive: true,
        dashboard: false,
        memberManage: false,
        orderManage: false,
        productManage: false,
        categoryManage: false,
        bannerManage: false,
        marketingManage: false,
        inventoryManage: false,
        returnManage: false,
        accountManage: false,
        permissionManage: false,
      },

      roles: [],
      currentPage: 1,
      itemsPerPage: 5,

      // 固定四個角色
      fixedRoles: [
        { RoleName: "平台總管", order: 1 },
        { RoleName: "高階主管", order: 2 },
        { RoleName: "中階主管", order: 3 },
        { RoleName: "一般員工", order: 4 },
      ],

      // 📋 後台所有可用的頁面（從 AdminLayout.vue 提取）
      availablePages: [
        {
          key: "dashboard",
          label: "儀表板",
        },
        {
          key: "customer",
          label: "會員管理",
        },
        {
          key: "order",
          label: "訂單管理",
        },
        {
          key: "products",
          label: "商品管理",
        },
        {
          key: "category",
          label: "類別管理",
        },
        {
          key: "banner",
          label: "橫幅管理",
        },
        {
          key: "market",
          label: "行銷管理",
        },
        {
          key: "sale",
          label: "庫存管理",
        },
        {
          key: "return",
          label: "退換貨管理",
        },
        {
          key: "account",
          label: "管理帳號設定",
        },
        {
          key: "permissions",
          label: "管理權限設定",
        },
      ],

      // 🔗 API 欄位與前端 key 的對應關係
      permissionFieldMap: {
        Dashboard: "dashboard",
        MemberManage: "customer",
        OrderManage: "order",
        ProductManage: "products",
        CategoryManage: "category",
        BannerManage: "banner",
        MarketingManage: "market",
        InventoryManage: "sale",
        ReturnManage: "return",
        AccountManage: "account",
        PermissionManage: "permissions",
      },

      // 🔗 availablePages key 到 formData 布爾值欄位的對應
      pageKeyToFieldMap: {
        dashboard: "dashboard",
        customer: "memberManage",
        order: "orderManage",
        products: "productManage",
        category: "categoryManage",
        banner: "bannerManage",
        market: "marketingManage",
        sale: "inventoryManage",
        return: "returnManage",
        account: "accountManage",
        permissions: "permissionManage",
      },
    };
  },

  computed: {
    filteredRoles() {
      let result = this.roles;

      // 搜尋
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter((role) =>
          role.RoleName.toLowerCase().includes(query),
        );
      }

      // 篩選
      if (this.activeFilter === "active") {
        result = result.filter((role) => role.IsActive);
      } else if (this.activeFilter === "inactive") {
        result = result.filter((role) => !role.IsActive);
      }

      return result;
    },

    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredRoles.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredRoles.length / this.itemsPerPage);
    },

    stats() {
      return {
        total: this.roles.length,
        active: this.roles.filter((r) => r.IsActive).length,
        inactive: this.roles.filter((r) => !r.IsActive).length,
      };
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    activeFilter() {
      this.currentPage = 1;
    },
    "formData.RoleName"(newVal) {
      // 當選擇平台總管時，自動勾選所有權限
      if (newVal === "平台總管") {
        this.formData.dashboard = true;
        this.formData.memberManage = true;
        this.formData.orderManage = true;
        this.formData.productManage = true;
        this.formData.categoryManage = true;
        this.formData.bannerManage = true;
        this.formData.marketingManage = true;
        this.formData.inventoryManage = true;
        this.formData.returnManage = true;
        this.formData.accountManage = true;
        this.formData.permissionManage = true;
      }
    },
  },

  methods: {
    formatDate(dateString) {
      return DateHelper.toTaiwanFormat(dateString);
    },

    async fetchRoles() {
      try {
        const res = await api.get("/permission/all"); // 呼叫新的權限 API

        if (res.data && res.data.data && Array.isArray(res.data.data)) {
          // 轉換新 API 格式 → 舊格式
          this.roles = res.data.data.map((profile) => {
            // 從布爾值字段提取權限清單
            const permissions = [];
            Object.entries(this.permissionFieldMap).forEach(
              ([apiField, key]) => {
                if (profile[apiField]) {
                  permissions.push(key);
                }
              },
            );

            return {
              Id: profile.Id,
              RoleName: profile.ProfileName,
              ProfileName: profile.ProfileName, // 保留原始字段
              IsActive: true, // API 中沒有 IsActive，預設為 true
              Permissions: permissions.join(","), // 轉為逗號分隔字串
              permissionObject: profile, // 保存原始的布爾值對象
              CreateDate: new Date().toISOString(),
            };
          });
          console.log("✅ 從 API 載入權限資料成功");
        }
      } catch (error) {
        console.warn("載入權限資料失敗：", error);
      }
    },

    // 初始化四個固定角色
    initializeFixedRoles() {
      this.roles = this.fixedRoles.map((role, index) => ({
        Id: index + 1,
        RoleName: role.RoleName,
        IsActive: true,
        Permissions: "", // 空的權限
        CreateDate: new Date().toISOString(),
      }));
    },

    handleSearch() {
      // 搜尋邏輯已在 computed 中處理
    },

    isRoleAlreadyAdded(roleName) {
      return this.roles.some((role) => role.RoleName === roleName);
    },

    editRolePermissions(role) {
      this.selectedRole = role;

      // API 返回的是大寫驼峰字段名，需要對應到小寫
      const apiToInternalMap = {
        Dashboard: "dashboard",
        MemberManage: "memberManage",
        OrderManage: "orderManage",
        ProductManage: "productManage",
        CategoryManage: "categoryManage",
        BannerManage: "bannerManage",
        MarketingManage: "marketingManage",
        InventoryManage: "inventoryManage",
        ReturnManage: "returnManage",
        AccountManage: "accountManage",
        PermissionManage: "permissionManage",
      };

      const permObj = role.permissionObject || {};

      this.formData = {
        RoleName: role.RoleName,
        IsActive: role.IsActive,
        dashboard: false,
        memberManage: false,
        orderManage: false,
        productManage: false,
        categoryManage: false,
        bannerManage: false,
        marketingManage: false,
        inventoryManage: false,
        returnManage: false,
        accountManage: false,
        permissionManage: false,
      };

      // 从 API 字段名映射到内部字段名
      Object.entries(apiToInternalMap).forEach(([apiField, internalField]) => {
        this.formData[internalField] = permObj[apiField] || false;
      });

      this.showPermissionModal = true;
    },

    closePermissionModal() {
      this.showPermissionModal = false;
      this.selectedRole = null;
      this.formData = {
        RoleName: "",
        IsActive: true,
        dashboard: false,
        memberManage: false,
        orderManage: false,
        productManage: false,
        categoryManage: false,
        bannerManage: false,
        marketingManage: false,
        inventoryManage: false,
        returnManage: false,
        accountManage: false,
        permissionManage: false,
      };
    },

    async saveRolePermissions() {
      // 平台總管權限寫死，儲存時不做任何事情
      if (this.selectedRole?.RoleName === "平台總管") {
        this.closePermissionModal();
        return;
      }

      try {
        // 直接從 formData 建立請求體
        const payload = {
          id: this.selectedRole.Id,
          dashboard: this.formData.dashboard,
          memberManage: this.formData.memberManage,
          orderManage: this.formData.orderManage,
          productManage: this.formData.productManage,
          categoryManage: this.formData.categoryManage,
          bannerManage: this.formData.bannerManage,
          marketingManage: this.formData.marketingManage,
          inventoryManage: this.formData.inventoryManage,
          returnManage: this.formData.returnManage,
          accountManage: this.formData.accountManage,
          permissionManage: this.formData.permissionManage,
        };

        await api.put("/permission/update", payload);

        showSuccess(
          "權限更新成功！",
          `${this.selectedRole.RoleName} 的權限已更新`,
        );

        // ✅ 重新抓取所有角色資料
        await this.fetchRoles();

        this.closePermissionModal();
      } catch (error) {
        const msg = error.response?.data?.message || "發生未知錯誤";
        showCustom({
          icon: "error",
          title: "操作失敗",
          text: msg,
        });
      }
    },

    async saveRole() {
      // 平台總管角色寫死，新建時不做任何事情
      if (this.formData.RoleName === "平台總管") {
        this.closeModal();
        return;
      }

      try {
        const payload = {
          roleName: this.formData.RoleName,
          isActive: this.formData.IsActive,
          dashboard: this.formData.dashboard,
          memberManage: this.formData.memberManage,
          orderManage: this.formData.orderManage,
          productManage: this.formData.productManage,
          categoryManage: this.formData.categoryManage,
          bannerManage: this.formData.bannerManage,
          marketingManage: this.formData.marketingManage,
          inventoryManage: this.formData.inventoryManage,
          returnManage: this.formData.returnManage,
          accountManage: this.formData.accountManage,
          permissionManage: this.formData.permissionManage,
        };

        await api.post("/role/add", payload);

        showSuccess("角色建立成功！");
        this.fetchRoles(); // 重新抓取角色資料
        this.closeModal();
      } catch (error) {
        const msg = error.response?.data?.message || "發生未知錯誤";
        showCustom({
          icon: "error",
          title: "操作失敗",
          text: msg,
        });
      }
    },

    async toggleRoleStatus(role) {
      // 平台總管不允許改變狀態
      if (role.RoleName === "平台總管") {
        showCustom({
          icon: "warning",
          title: "提示",
          text: "平台總管角色狀態無法更改",
        });
        return;
      }

      const action = role.IsActive ? "停用" : "啟用";
      const confirmResult = await showConfirm(
        `確定要${action}角色「${role.RoleName}」嗎？`,
      );

      if (!confirmResult.isConfirmed) return;

      try {
        // 如果有 permissionObject，用它來構建請求體
        const payload = role.permissionObject
          ? { ...role.permissionObject, id: role.Id }
          : {
              id: role.Id,
              dashboard: false,
              memberManage: false,
              orderManage: false,
              productManage: false,
              categoryManage: false,
              bannerManage: false,
              marketingManage: false,
              inventoryManage: false,
              returnManage: false,
              accountManage: false,
              permissionManage: false,
            };

        // 注意：如果後端只有權限管理 API 沒有單獨的狀態更新 API，
        // 可能需要對接單獨的端點或由後端判斷
        await api.put("/permission/update", payload);

        // ✅ 更新前端顯示
        role.IsActive = !role.IsActive;
        role.UpdateDate = new Date().toLocaleString("zh-TW");

        showSuccess(
          `已${action}角色`,
          `角色 ${role.RoleName} 現在為「${
            role.IsActive ? "已啟用" : "已停用"
          }」狀態。`,
        );
      } catch (error) {
        const msg = error.response?.data?.message ?? "更新狀態失敗";
        showCustom({
          icon: "error",
          title: "錯誤",
          text: msg,
        });
      }
    },

    closeModal() {
      this.showAddModal = false;
      this.editingRole = null;
      this.formData = {
        RoleName: "",
        IsActive: true,
        dashboard: false,
        memberManage: false,
        orderManage: false,
        productManage: false,
        categoryManage: false,
        bannerManage: false,
        marketingManage: false,
        inventoryManage: false,
        returnManage: false,
        accountManage: false,
        permissionManage: false,
      };
    },

    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
  },

  mounted() {
    this.fetchRoles(); // ✅ 載入畫面就抓角色資料
  },
};
